// ─────────────────────────────────────────────────────────────────────────────
// src/db/localDB.js
// Base de datos local del navegador usando Dexie.js (wrapper de IndexedDB).
// Almacena ventas pendientes de sincronizar y cachés de productos/clientes.
// ─────────────────────────────────────────────────────────────────────────────

import Dexie from 'dexie';

export const db = new Dexie('ERPGipSoSOffline');


// ─── Versión 1: esquema original ───────────────────────────────────────────
db.version(1).stores({
  pending_sales:    '++id, created_at, sync_status, retry_count, user_id',
  cached_products:  'id, user_id, code, name, is_active, updated_at',
  cached_customers: 'id, user_id, name, is_active',
  sync_log:         '++id, type, status, timestamp, error_message'
});

// ─── Versión 2: agrega sucursal_id en pending_sales ────────────────────────
// Dexie migrará automáticamente el IndexedDB existente sin borrar datos.
db.version(2).stores({
  /**
   * Cola de ventas pendientes de sincronizar con el servidor.
   * sync_status: 'pending' | 'syncing' | 'synced' | 'error' | 'conflict'
   * sucursal_id: ID de la sucursal física que originó la venta (persistido en localStorage)
   */
  pending_sales: '++id, created_at, sync_status, retry_count, user_id, sucursal_id',

  /**
   * Caché de productos para búsqueda offline en el POS.
   * Se actualiza cada vez que el usuario abre la app estando online.
   */
  cached_products: 'id, user_id, code, name, is_active, updated_at',

  /**
   * Caché de clientes para asignar a ventas offline.
   */
  cached_customers: 'id, user_id, name, is_active',

  /**
   * Log de sincronizaciones para auditoría y debugging.
   */
  sync_log: '++id, type, status, timestamp, error_message'
});

// ─── Versión 3: índice compuesto [user_id+sync_status] ─────────────────────
// getPendingSales() filtra siempre por ambos campos a la vez.
// Sin índice compuesto, Dexie hace un full-scan → advertencia de performance.
// Sintaxis Dexie: '[campo1+campo2]' crea un MultiEntry compound index.
db.version(3).stores({
  pending_sales:    '++id, created_at, sync_status, retry_count, user_id, sucursal_id, [user_id+sync_status]',
  cached_products:  'id, user_id, code, name, is_active, updated_at',
  cached_customers: 'id, user_id, name, is_active',
  sync_log:         '++id, type, status, timestamp, error_message'
});


// ─── Helpers de acceso ─────────────────────────────────────────────

/**
 * Lee el sucursal_id del dispositivo actual desde localStorage.
 * Se guarda al hacer login y es la fuente de verdad para todas las ventas offline.
 * @returns {number|null}
 */
export function getSucursalId() {
  const id = localStorage.getItem('sucursal_id');
  return id ? parseInt(id, 10) : null;
}

/**
 * Guarda una venta en la cola offline.
 * Adjunta automáticamente el sucursal_id del dispositivo para que el backend
 * sepa de qué sucursal proviene el lote al sincronizar.
 *
 * @param {object} saleData - Los mismos datos que se enviarían a POST /api/sales
 * @param {number} userId   - ID del usuario actual (para filtrar en multi-usuario)
 */
export async function addPendingSale(saleData, userId) {
  const sucursalId = getSucursalId();
  return await db.pending_sales.add({
    data: { ...saleData, sucursal_id: sucursalId },   // ← sucursal_id viaja dentro del payload
    user_id:     userId,
    sucursal_id: sucursalId,                           // ← también indexado para consultas Dexie
    created_at:  new Date().toISOString(),
    sync_status: 'pending',
    retry_count: 0,
    last_error:  null
  });
}

/**
 * Obtiene todas las ventas pendientes del usuario actual.
 */
export async function getPendingSales(userId) {
  return await db.pending_sales
    .where({ user_id: userId, sync_status: 'pending' })
    .sortBy('created_at');
}

/**
 * Cuenta las ventas pendientes de un usuario.
 */
export async function countPendingSales(userId) {
  return await db.pending_sales
    .where({ user_id: userId, sync_status: 'pending' })
    .count();
}

/**
 * Guarda el caché de productos en IndexedDB.
 * Reemplaza todo el caché existente del usuario.
 * @param {Array} products - Array de productos del servidor
 * @param {number} userId - ID del usuario
 */
export async function cacheProducts(products, userId) {
  // Borramos el caché anterior del usuario y guardamos el nuevo
  await db.cached_products.where('user_id').equals(userId).delete();
  const toCache = products.map(p => ({ ...p, user_id: userId }));
  await db.cached_products.bulkPut(toCache);
}

/**
 * Guarda el caché de clientes en IndexedDB.
 */
export async function cacheCustomers(customers, userId) {
  await db.cached_customers.where('user_id').equals(userId).delete();
  const toCache = customers.map(c => ({ ...c, user_id: userId }));
  await db.cached_customers.bulkPut(toCache);
}

/**
 * Busca productos en el caché local (para el POS offline).
 * @param {string} term - Término de búsqueda (nombre o código)
 * @param {number} userId
 */
export async function searchCachedProducts(term, userId) {
  const termLower = term.toLowerCase();
  return await db.cached_products
    .where('user_id').equals(userId)
    .and(p =>
      p.is_active &&
      (p.name.toLowerCase().includes(termLower) ||
       p.code.toLowerCase().includes(termLower))
    )
    .toArray();
}

/**
 * Busca clientes en el caché local.
 */
export async function searchCachedCustomers(term, userId) {
  const termLower = term.toLowerCase();
  return await db.cached_customers
    .where('user_id').equals(userId)
    .and(c => c.is_active && c.name.toLowerCase().includes(termLower))
    .toArray();
}

export default db;
